import React, { Fragment, useEffect, useState } from 'react';
import {
    Row, Col, Typography, Button, Upload, Flex, Spin, Alert,
    Card, Form, Input, InputNumber, DatePicker, Switch, Select,
    Tag, Divider, List, Progress,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import UploadIcon from '../../components/icon/UploadIcon';
import { uploadExtract, confirmExtraction, setAiExtraction, setIsSuccess } from './redux/slice';
import { primary, secondary, warning } from '../../utils/theme/config';

const { Title, Text } = Typography;

const INTERVAL_OPTIONS = [
    { value: 'monthly', label: 'Monatlich' },
    { value: 'quarterly', label: 'Quartalsweise' },
    { value: 'yearly', label: 'Jährlich' },
    { value: 'once', label: 'Einmalig' },
];

const ConfidenceBar = ({ value }) => {
    const pct = Math.round((value || 0) * 100);
    const color = pct >= 80 ? primary : pct >= 50 ? '#fa8c16' : secondary;
    return (
        <Flex align='center' gap={6}>
            <Progress percent={pct} size='small' strokeColor={color} style={{ flex: 1, marginBottom: 0 }} />
            <Text style={{ fontSize: 12, color, minWidth: 38 }}>{pct}%</Text>
        </Flex>
    );
};

const ContractAiReview = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const auth = useSelector(state => state.auth);
    const contracts = useSelector(state => state.contracts);
    const { loading, aiExtraction, isSuccess } = contracts;
    const organizer = auth?.decodeToken?.organizer;

    const [uploadFile, setUploadFile] = useState(null);
    const [extracted, setExtracted] = useState(null);

    useEffect(() => {
        return () => {
            dispatch(setAiExtraction(null));
            dispatch(setIsSuccess(false));
        };
    }, []);

    useEffect(() => {
        if (aiExtraction) {
            setExtracted(aiExtraction);
            const fields = aiExtraction.extractedData || aiExtraction.fields || aiExtraction;
            form.setFieldsValue({
                partner: fields.partner,
                contractNumber: fields.contractNumber,
                customerNumber: fields.customerNumber,
                contactPerson: fields.contactPerson,
                startDate: fields.startDate ? dayjs(fields.startDate) : null,
                endDate: fields.endDate ? dayjs(fields.endDate) : null,
                noticePeriod: fields.noticePeriod,
                autoRenewal: fields.autoRenewal,
                costAmount: fields.costAmount,
                costInterval: fields.costInterval,
                tags: fields.suggestedTags || [],
            });
        }
    }, [aiExtraction]);

    useEffect(() => {
        if (isSuccess) {
            dispatch(setIsSuccess(false));
            navigate('/contract-manager/list');
        }
    }, [isSuccess]);

    const handleUpload = () => {
        if (!uploadFile) return;
        dispatch(uploadExtract({ file: uploadFile, organizer }));
    };

    const handleConfirm = (values) => {
        const payload = {
            ...values,
            startDate: values.startDate ? values.startDate.toISOString() : undefined,
            endDate: values.endDate ? values.endDate.toISOString() : undefined,
            organizer,
            contractId: id || undefined,
            extractionId: extracted?.extractionId,
        };
        dispatch(confirmExtraction(payload));
    };

    const handleReset = () => {
        setUploadFile(null);
        setExtracted(null);
        form.resetFields();
        dispatch(setAiExtraction(null));
    };

    const fields = extracted?.extractedData || extracted?.fields || extracted || {};
    const confidence = extracted?.extractedData?.confidence || extracted?.confidence || {};
    const clauses = extracted?.importantClauses || [];
    const risks = extracted?.risks || [];
    const suggestedCustomFields = extracted?.suggestedCustomFields || [];

    return (
        <Fragment>
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col>
                    <Title level={4} type='success'>KI-Import</Title>
                </Col>
                {extracted && (
                    <Col>
                        <Button onClick={handleReset}>Neu starten</Button>
                    </Col>
                )}
            </Row>

            <Alert
                className='padding-top-1'
                message='Hinweis: KI-Ergebnisse müssen vor der Übernahme geprüft werden. Die Extraktion kann Fehler enthalten.'
                type='warning'
                showIcon
                style={{ marginTop: 12, marginBottom: 12 }}
            />

            {!extracted ? (
                <Card className='border-green' style={{ marginTop: 12 }}>
                    <Title level={5} type='success'>Vertragsdokument hochladen</Title>
                    <Upload.Dragger
                        multiple={false}
                        showUploadList={true}
                        beforeUpload={(file) => {
                            setUploadFile(file);
                            return false;
                        }}
                        accept='.pdf,.png,.jpg,.jpeg,.webp'
                        maxCount={1}
                    >
                        <p className='ant-upload-drag-icon'>
                            <UploadIcon size={30} />
                        </p>
                        <Text className='ant-upload-text' strong>PDF oder Bild hierher ziehen oder klicken</Text>
                        <p className='ant-upload-hint'>Unterstützt: PDF, PNG, JPG, WebP</p>
                    </Upload.Dragger>

                    <Flex justify='center' style={{ marginTop: 16 }}>
                        <Button
                            type='primary'
                            disabled={!uploadFile}
                            loading={loading}
                            onClick={handleUpload}
                            style={{ minWidth: 160 }}
                        >
                            Auswerten
                        </Button>
                    </Flex>

                    {loading && (
                        <Flex justify='center' align='center' vertical gap={12} style={{ marginTop: 24 }}>
                            <Spin size='large' />
                            <Text type='secondary'>Dokument wird analysiert ...</Text>
                        </Flex>
                    )}
                </Card>
            ) : (
                <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                    {/* Left: Extracted Data with Confidence */}
                    <Col xxl={10} xl={10} lg={12} md={24} sm={24} xs={24}>
                        <Card title={<Text strong>Erkannte Felder (KI)</Text>} className='border-green'>
                            {[
                                { key: 'partner', label: 'Vertragspartner' },
                                { key: 'contractNumber', label: 'Vertragsnummer' },
                                { key: 'customerNumber', label: 'Kundennummer' },
                                { key: 'contactPerson', label: 'Ansprechpartner' },
                                { key: 'startDate', label: 'Startdatum' },
                                { key: 'endDate', label: 'Enddatum' },
                                { key: 'noticePeriod', label: 'Kündigungsfrist (Tage)' },
                                { key: 'autoRenewal', label: 'Auto-Verlängerung' },
                                { key: 'costAmount', label: 'Kosten' },
                                { key: 'costInterval', label: 'Intervall' },
                            ].map(({ key, label }) => (
                                <div key={key} style={{ marginBottom: 12 }}>
                                    <Text type='secondary' style={{ fontSize: 12 }}>{label}</Text>
                                    <Flex justify='space-between' align='center'>
                                        <Text strong>
                                            {fields[key] !== undefined && fields[key] !== null && fields[key] !== ''
                                                ? String(fields[key])
                                                : <Text type='secondary'>—</Text>}
                                        </Text>
                                    </Flex>
                                    {confidence[key] !== undefined && (
                                        <ConfidenceBar value={confidence[key]} />
                                    )}
                                </div>
                            ))}

                            {fields.suggestedTags && fields.suggestedTags.length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <Text type='secondary' style={{ fontSize: 12 }}>Vorgeschlagene Tags</Text>
                                    <Flex wrap gap={4} style={{ marginTop: 4 }}>
                                        {fields.suggestedTags.map((t) => <Tag key={t}>{t}</Tag>)}
                                    </Flex>
                                </div>
                            )}
                        </Card>

                        {clauses.length > 0 && (
                            <Card title={<Text strong>Wichtige Klauseln</Text>} className='border-green' style={{ marginTop: 12 }}>
                                <List
                                    dataSource={clauses}
                                    renderItem={(clause) => (
                                        <List.Item>
                                            <Text>{clause}</Text>
                                        </List.Item>
                                    )}
                                    size='small'
                                />
                            </Card>
                        )}

                        {risks.length > 0 && (
                            <Card
                                title={<Text strong style={{ color: secondary }}>Risiken</Text>}
                                style={{ marginTop: 12, borderColor: secondary }}
                            >
                                <List
                                    dataSource={risks}
                                    renderItem={(risk) => (
                                        <List.Item>
                                            <Flex align='flex-start' gap={8}>
                                                <Text style={{ color: secondary }}>!</Text>
                                                <Text>{typeof risk === 'string' ? risk : risk.description}</Text>
                                            </Flex>
                                        </List.Item>
                                    )}
                                    size='small'
                                />
                            </Card>
                        )}

                        {suggestedCustomFields.length > 0 && (
                            <Card title={<Text strong>Vorgeschlagene Zusatzfelder</Text>} className='border-green' style={{ marginTop: 12 }}>
                                {suggestedCustomFields.map((f, idx) => (
                                    <div key={idx} style={{ padding: '4px 0' }}>
                                        <Text strong>{f.fieldName}</Text>
                                        <Text type='secondary'> ({f.fieldType}): </Text>
                                        <Text>{String(f.value)}</Text>
                                    </div>
                                ))}
                            </Card>
                        )}
                    </Col>

                    {/* Right: Editable Confirmation Form */}
                    <Col xxl={14} xl={14} lg={12} md={24} sm={24} xs={24}>
                        <Card title={<Text strong>Daten prüfen & übernehmen</Text>} className='border-green'>
                            <Form
                                form={form}
                                layout='vertical'
                                onFinish={handleConfirm}
                            >
                                <Row gutter={12}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='partner' label='Vertragspartner'>
                                            <Input placeholder='Partner' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='contractNumber' label='Vertragsnummer'>
                                            <Input placeholder='Nr.' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='customerNumber' label='Kundennummer'>
                                            <Input placeholder='Kundennr.' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='contactPerson' label='Ansprechpartner'>
                                            <Input placeholder='Name' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='startDate' label='Startdatum'>
                                            <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='endDate' label='Enddatum'>
                                            <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='noticePeriod' label='Kündigungsfrist (Tage)'>
                                            <InputNumber style={{ width: '100%' }} min={0} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='autoRenewal' label='Auto-Verlängerung' valuePropName='checked'>
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='costAmount' label='Kostenbetrag (€)'>
                                            <InputNumber style={{ width: '100%' }} prefix='€' min={0} step={0.01} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name='costInterval' label='Kostenintervall'>
                                            <Select allowClear options={INTERVAL_OPTIONS} placeholder='Intervall' />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item name='tags' label='Tags'>
                                            <Select mode='tags' placeholder='Tags' />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider />

                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Button block danger type='primary' onClick={handleReset}>
                                            Neu starten
                                        </Button>
                                    </Col>
                                    <Col span={12}>
                                        <Button block type='primary' htmlType='submit' loading={loading}>
                                            Übernehmen & Speichern
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            )}
        </Fragment>
    );
};

export default ContractAiReview;
